import SegmentMetricTable, { JsonRouteMetric, timeOfDayToJson } from "./SegmentMetricTable";
import {
    RiskCategoryMetrics,
    RouteInstructionsAndMetrics,
    RouteMetrics,
    RouteRequestInputType,
    TimeRangeMetrics,
    Trip,
    Waypoint,
} from "../../generated/gpb/api/route";
import { categoryToRiskLevel } from "../../app/riskLevel";
import { RiskCategory, riskCategoryFromJSON, } from "../../generated/gpb/api/risk";
import TripSupportDialog, { dateToString, getEndRouteName, getStartRouteName, } from "../trips/TripSupportDialog";
import {
    ADVISORY_ROUTE_TEXT,
    CONTACT_SUPPORT,
    CUSTOM_ROUTE_TEXT,
    DAILY_END_TIME,
    DAILY_FINAL_END_TIME,
    DAILY_START_TIME,
    DAY_1_START_TIME,
    DESTINATION,
    FAILED_TO_LOAD_ERRORS,
    GENERIC_ROUTE_ERROR_TEXT,
    HEIGHT_TOOLTIP,
    INSTRUCTION_DETAILS,
    MAX_ACCEPTABLE_IMPACT,
    MAX_SPEED,
    METRICS,
    REQUEST_DETAILS,
    REQUEST_TYPE,
    START_STRING,
    STARTING_POINT,
    UNABLE_TO_GENERATE_ROUTE_CITIES,
    UNABLE_TO_GENERATE_ROUTE_HEIGHT,
    UNABLE_TO_GENERATE_ROUTE_TIPS_HEADER,
    UNABLE_TO_GENERATE_ROUTE_TOLLS,
    WEIGHT_TOOLTIP,
    YOUR_REQUEST_MESSAGE,
} from "../UserVisibleStrings";
import { DateTime } from "luxon";
import { ABSOLUTE_MAX_SPEED } from "../trips/form/TripFormTypeConversions";
import React from "react";
import { Button, CircularProgress, Tab, Tabs } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCalculator,
    faCircleExclamation,
    faDirections,
    faPenToSquare
} from "@fortawesome/sharp-duotone-light-svg-icons";
import { getErrors } from "../trips/tripUtils";
import { RouteMetricsContent } from "./RouteMetricsDialog";
import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import { point, polygon } from "@turf/helpers";

function getRiskLevel(
    category: RiskCategory | undefined,
    name: string
): JsonRouteMetric | undefined
{
    if ( category === undefined ) return undefined;
    return {
        name: name,
        value: ( () =>
        {
            const categoryString = categoryToRiskLevel(
                riskCategoryFromJSON( category )
            ).toString();
            // This is annoying to add, but despite typescript, it manages to sometimes treat
            // category as an integer and sometimes as a string, so this check ensures that the
            // RiskCategory is always an integer for the formatting below.
            let myIndex = category;
            if ( typeof myIndex === "string" )
            {
                myIndex = Object.values( RiskCategory ).findIndex( ( e ) => e === category );
            }
            return ( myIndex >= 1 ? myIndex - 1 + ": " : "" ) + categoryString;
        } )(),
    };
}

// These boundaries are not exact, because the wilma surface is not a polygon.
const wilmaImpactSurface = polygon( [
    [
        [ -138.36, 53.15 ],
        [ -133.92, 53.95 ],
        [ -132.15, 54.18 ],
        [ -128.95, 54.66 ],
        [ -125.04, 55.31 ],
        [ -120.92, 55.786 ],
        [ -113.04, 56.43 ],
        [ -106.04, 56.83 ],
        [ -98.00, 57.16 ],
        [ -87.996, 57.08 ],
        [ -76.49, 56.35 ],
        [ -72.17, 56.026 ],
        [ -65.62, 53.37 ],
        [ -59.038, 54.3607 ],
        [ -69.04, 20.16 ],
        [ -97.91, 22.61 ],
        [ -105.64, 22.25 ],
        [ -126.38, 19.14 ],
        [ -138.36, 53.15 ],
    ],
] );

export function IsPointWithinImpactSurface( latitude: number, longitude: number ): boolean
{
    return booleanPointInPolygon( point( [ longitude, latitude ] ), wilmaImpactSurface );
}

function getWaypointDetails( wp: Waypoint ): JsonRouteMetric
{
    let description = "";
    if ( wp.overnightStop !== undefined )
    {
        const nights = wp.overnightStop.minNights === undefined ? 1 : wp.overnightStop.minNights;

        description +=
      "Overnight stop [" + nights + " night" + ( nights > 1 ? "s" : "" ) + "]. ";
    }
    if ( wp.pitStop !== undefined )
    {
        const minutes = wp.pitStop.minDurationMinutes ?? 0;
        description +=
      "Pit stop [" + minutes + " minute" + ( minutes > 1 ? "s" : "" ) + "]. ";
    }
    return {
        name: "Waypoint: " + wp.location?.locationName,
        value: description,
    };
}

export function getRouteInputs(
    trip: Trip | undefined,
    forecastTime: Date | undefined
)
{
    const data: Array<JsonRouteMetric> = [];
    if (
        trip === undefined ||
        trip.routeRequestInput === undefined
    )
    {
        return data;
    }

    const routeRequest = trip?.routeRequestInput;

    data.push( {
        name: REQUEST_TYPE,
        value: routeRequest?.requestType === RouteRequestInputType.ADVISORY ? ADVISORY_ROUTE_TEXT : CUSTOM_ROUTE_TEXT,
    } );

    // Push starting point and destination first
    data.push( {
        name: STARTING_POINT,
        value: getStartRouteName( routeRequest ) ?? "Undefined",
    } );

    // Add intermediate waypoints, if any
    routeRequest?.waypoints?.forEach( ( wp, index ) =>
    {
        if ( index !== 0 && index !== routeRequest?.waypoints.length - 1 )
        {
            data.push( getWaypointDetails( wp ) );
        }
    } );

    data.push( {
        name: DESTINATION,
        value: getEndRouteName( routeRequest ) ?? "Undefined",
    } );

    data.push( {
        name: "Waypoint Count",
        value: Math.max( 0, routeRequest.waypoints?.length - 2 ),
        digits: 0,
    } );

    const riskLevel = getRiskLevel( routeRequest.acceptableRiskLevelCeiling, MAX_ACCEPTABLE_IMPACT );
    if ( riskLevel !== undefined && routeRequest?.requestType !== RouteRequestInputType.ADVISORY )
    {
        data.push( riskLevel );
    }

    const timeOfDayStart = timeOfDayToJson( DAILY_START_TIME, routeRequest.desiredStartTimeOfDay );
    if ( timeOfDayStart !== undefined )
    {
        data.push( timeOfDayStart );
    }

    const dayOneStartTime = timeOfDayToJson( DAY_1_START_TIME, routeRequest?.waypoints[0].startTime );
    if ( dayOneStartTime !== undefined )
    {
        data.push( dayOneStartTime );
    }

    const timeOfDayEnd = timeOfDayToJson( DAILY_END_TIME, routeRequest.desiredEndTimeOfDay );
    if ( timeOfDayEnd !== undefined && routeRequest?.requestType !== RouteRequestInputType.ADVISORY )
    {
        data.push( timeOfDayEnd );
    }

    if ( routeRequest.startDate !== undefined )
    {
        data.push( {
            name: START_STRING + " Date",
            value: dateToString( routeRequest.startDate ),
        } );
    }
    const finalEndTime = timeOfDayToJson( DAILY_FINAL_END_TIME, routeRequest.finalEndTimeOfDay );
    if ( finalEndTime !== undefined && routeRequest?.requestType !== RouteRequestInputType.ADVISORY )
    {
        data.push( finalEndTime );
    }
    if ( routeRequest.maxSpeed !== undefined )
    {
        if ( routeRequest.maxSpeed >= ABSOLUTE_MAX_SPEED )
        {
            data.push( { name: MAX_SPEED, value: "Use posted speed limit" } );
        }
        else
        {
            data.push( {
                name: MAX_SPEED,
                value: routeRequest.maxSpeed,
                unit: "mph",
            } );
        }
    }
    if ( routeRequest.maxHeightFt !== undefined )
    {
        const heightInFeet = routeRequest.maxHeightFt;
        const feet = Math.floor( heightInFeet );
        const inches = Math.round( ( heightInFeet - feet ) * 12 );

        data.push( {
            name: HEIGHT_TOOLTIP,
            value: `${feet} feet ${inches} inches`,
        } );
    }
    if ( routeRequest.maxWeightLbs !== undefined )
    {
        data.push( {
            name: WEIGHT_TOOLTIP,
            value: `${routeRequest.maxWeightLbs} pounds`,
        } );
    }
    if ( routeRequest.submissionTime !== undefined )
    {
        const date = Date.parse( routeRequest.submissionTime.toString() );
        const dateTime = formatDateTime( DateTime.fromMillis( date ) );
        data.push( { name: "Submission Time", value: dateTime } );
    }
    if ( forecastTime )
    {
        const date = Date.parse( forecastTime.toString() );
        const dateTime = formatDateTime( DateTime.fromMillis( date ) );
        data.push( { name: "Forecast Time", value: dateTime } );
    }
    const avoidBool = ( Boolean )( routeRequest.avoidTolls ) ? "True" : "False";
    data.push( { name: "Avoid Tolls", value: avoidBool } );
    const USBool = ( Boolean )( routeRequest?.includeUs ) ? "True" : "False";
    data.push( { name: "Routing in the United States", value: USBool } );
    const mexicoBool = ( Boolean )( routeRequest?.includeMexico ) ? "True" : "False";
    data.push( { name: "Routing in Mexico", value: mexicoBool } );
    const canadaBool = ( Boolean )( routeRequest?.includeCanada ) ? "True" : "False";
    data.push( { name: "Routing in Canada", value: canadaBool } );

    return data;
}
export function formatDateTime( dateTime: DateTime ): string
{
    return dateTime.toLocal().toFormat( "ccc, LLL dd hh:mm a ZZZZ" );
}

export const handlePrint = () =>
{
    const content = document.getElementById( "contentsToPrint" );

    if ( !content )
    {
        alert( "Content to print not found" );
        return;
    }

    const pri = document.createElement( "iframe" );

    pri.style.position = "absolute";
    pri.style.width = "0px";
    pri.style.height = "0px";
    pri.style.border = "none";

    document.body.appendChild( pri );

    const doc = pri.contentWindow?.document;

    if ( doc )
    { // Check if contentWindow is available
        doc.open();

        // Write content to iframe
        doc.write( content.innerHTML );
        doc.close();

        // Trigger the print
        pri.contentWindow.focus();
        pri.contentWindow.print();

        // Clean up after printing
        setTimeout( () =>
        {
            document.body.removeChild( pri );
        }, 1000 );
    }
    else
    {
        alert( "Failed to access the printing frame. Please try again or contact support if the problem persists." );
    }
};



export const handleEmail = ( metricsAndInstructions: RouteInstructionsAndMetrics | undefined, trip: Trip | undefined ) =>
{
    // Initialize email body content
    let emailBody = "Route Overview\n\n";

    // Loop over metricsAndInstructions and generate the content
    if ( metricsAndInstructions?.instructions !== undefined && metricsAndInstructions?.instructions.length > 0 )
    {
        metricsAndInstructions.instructions.forEach( ( segmentInstructions, indexSegment ) =>
        {
            // If no instructions, add a line break
            if ( segmentInstructions.instructions.length === 0 )
            {
                emailBody += "\n";
            }
            else
            {
                // Add a title for the segment
                const segmentTitle = getSegmentTitle( indexSegment, trip );
                emailBody += `${segmentTitle}\n`;

                // Create a numbered list for instructions
                segmentInstructions.instructions.forEach( ( instruction, index ) =>
                {
                    emailBody += `${index + 1}. ${instruction.instruction}\n`;
                } );
                emailBody += "\n";
            }
        } );
    }
    else
    {
        emailBody += "No instructions available.\n";
    }

    // Create a mailto link to open email client with the content
    // Open the user's email client with the mailto link
    window.location.href = `mailto:?subject=Route Overview&body=${encodeURIComponent( emailBody )}`;
};

export function getSegmentTitle(
    segmentIndex: number,
    trip: Trip | undefined
)
{
    const defaultTitle = "Segment " + segmentIndex;
    if (
        trip === undefined ||
        trip.routeRequestInput === undefined
    )
    {
        return defaultTitle;
    }
    const waypoints = trip.routeRequestInput?.waypoints;
    if (
        waypoints === undefined ||
        segmentIndex >= waypoints.length ||
        segmentIndex < 0
    )
    {
        return defaultTitle;
    }
    // segmentIndex/waypoints is 0 indexed each title is from the current index to current + 1
    const start = waypoints[segmentIndex];
    const dest = waypoints[segmentIndex + 1];
    if (
        start === undefined ||
        dest === undefined ||
        start.location === undefined ||
        dest.location === undefined
    )
    {
        return defaultTitle;
    }
    return start.location?.locationName + " -> " + dest.location?.locationName;
}

export function generateErrorContent(
    requestTableData: Array<JsonRouteMetric> | undefined,
    trip: Trip | undefined,
    openHelp: boolean,
    setOpenHelp: React.Dispatch<React.SetStateAction<boolean>>,
)
{
    let errors: Array<string> = [ FAILED_TO_LOAD_ERRORS ];
    if ( trip !== undefined )
    {
        errors = getErrors( trip );
    }
    let errorText: string | undefined = undefined;
    let nonDescriptMessage = false;
    if ( errors.length > 0 )
    {
        errorText = "Error: ";
        for ( let i = 0; i < errors.length; i++ )
        {
            errorText += errors[i];
            if ( i === errors.length - 1 )
            {
                errorText += ", ";
            }
            if ( errors[i] === "Unable to generate route" || errors[i].startsWith( "Failed to generate route variations" ) ) nonDescriptMessage = true;
        }
    }
    return (

        <React.Fragment>
            <div style = {{ paddingBottom: "5px" }}>
                <FontAwesomeIcon icon = {faCircleExclamation} size = "xl" color = "red" fixedWidth/>
                {errorText !== undefined ? errorText : GENERIC_ROUTE_ERROR_TEXT}
            </div>
            {nonDescriptMessage && (
                <React.Fragment>
                    <p/>
                    <div>
                        {unableToGenerateRouteTips( trip )}
                    </div>
                    <p/>
                </React.Fragment>
            )}
            <span>For more tips, check out </span>
            <a href = "https://rvweather.com/sureroute-error-info/" target = "_blank" rel = "noopener noreferrer">this link</a>
            <span> or contact technical support with the button below</span>
            <Button
                color = "primary"
                variant = "contained"
                onClick = {( event ) =>
                {
                    event.stopPropagation();
                    setOpenHelp( true );
                }}
            >
                {CONTACT_SUPPORT}
            </Button>
            {requestTableData !== undefined &&
            <React.Fragment>
                <p/>
                <div>{YOUR_REQUEST_MESSAGE}</div>
                <hr style = {{ borderTop: "1px dotted #999", margin: "10px 0" }}/>
                <SegmentMetricTable
                    routeMetrics = {requestTableData}
                    heading = {REQUEST_DETAILS}
                />
            </React.Fragment>}
            {openHelp && (
                <TripSupportDialog
                    helpInfo = {{
                        trip: trip,
                        tripID: trip?.Id,
                        input: trip?.routeRequestInput,
                        errors: errors
                    }}
                    open = {openHelp}
                    onClose = {() => setOpenHelp( false )}
                /> )}
        </React.Fragment>
    );
}

export function unableToGenerateRouteTips( trip: Trip | undefined )
{
    const tips = [
        { show: true, text: UNABLE_TO_GENERATE_ROUTE_CITIES },
        { show: trip?.routeRequestInput?.avoidTolls, text: UNABLE_TO_GENERATE_ROUTE_TOLLS },
        { show: trip?.routeRequestInput?.maxHeightFt !== undefined, text: UNABLE_TO_GENERATE_ROUTE_HEIGHT },
    ];

    return (
        <React.Fragment>
            {UNABLE_TO_GENERATE_ROUTE_TIPS_HEADER}
            <ul>
                {tips
                    .filter( t => t.show )
                    .map( t => <li key = {t.text}>{t.text}</li> )}
            </ul>
        </React.Fragment>
    );
}

export function generateContent(
    isLoading: boolean,
    isError: boolean,
    metricsAndInstructions: RouteInstructionsAndMetrics | undefined,
    selectedTab: number,
    setSelectedTab: React.Dispatch<React.SetStateAction<number>>,
    requestTableData: Array<JsonRouteMetric>,
    trip: Trip | undefined,
    openHelp: boolean,
    setOpenHelp: React.Dispatch<React.SetStateAction<boolean>>,
    isMultistartEligible: boolean
)
{
    if ( isLoading )
    {
        return (
            <div style = {{ width: "100%", textAlign: "center" }}>
                <CircularProgress style = {{ margin: "auto" }}/>
            </div>
        );
    }
    if ( isError )
    {
        return generateErrorContent( requestTableData, trip, openHelp, setOpenHelp );
    }

    if ( isMultistartEligible )
    {
        return undefined;
    }
    return (
        <React.Fragment>
            <Tabs
                value = {selectedTab}
                onChange = {( _, newValue: number ) => setSelectedTab( newValue )}
                centered
            >
                <Tab
                    icon = {<FontAwesomeIcon icon = {faDirections} size = "lg" fixedWidth/>}
                    label = {INSTRUCTION_DETAILS}
                    iconPosition = "start"
                    sx = {{ fontSize: ".75rem" }}
                />
                <Tab
                    icon = {<FontAwesomeIcon icon = {faCalculator} size = "lg" fixedWidth/>}
                    label = {METRICS}
                    iconPosition = "start"
                    sx = {{ fontSize: ".75rem" }}
                />
                <Tab
                    icon = {<FontAwesomeIcon icon = {faPenToSquare} size = "lg" fixedWidth/>}
                    label = {REQUEST_DETAILS}
                    iconPosition = "start"
                    sx = {{ fontSize: ".75rem" }}
                />
            </Tabs>
            {selectedTab === 0 && (
                <div id = "contentsToPrint">
                    {metricsAndInstructions?.instructions?.map(
                        ( segmentInstructions, indexSegment ) =>
                        {
                            if ( segmentInstructions.instructions.length === 0 )
                            {
                                return <br key = {indexSegment + "br"}></br>;
                            }
                            return (
                                <div key = {indexSegment + "div"}>
                                    <h3 key = {"segment" + indexSegment}>
                                        {getSegmentTitle(
                                            indexSegment,
                                            trip
                                        )}
                                    </h3>
                                    <ol key = {indexSegment + "_ol"}>
                                        {segmentInstructions.instructions.map(
                                            ( instruction, index ) => (
                                                <li key = {"instruction" + index}>
                                                    {instruction.instruction}
                                                </li>
                                            )
                                        )}
                                    </ol>
                                </div>
                            );
                        }
                    )}
                </div>
            )}
            {selectedTab === 1 && (
                <RouteMetricsContent
                    routeMetrics = {metricsAndInstructions?.metrics ?? []}
                    showTemps
                />
            )}
            {selectedTab === 2 && requestTableData.length > 0 && (
                <SegmentMetricTable
                    routeMetrics = {requestTableData}
                    heading = {REQUEST_DETAILS}
                />
            )}
        </React.Fragment>
    );
}

export const MS_IN_HOUR = 3600000;
export const IMPERCEPTIBLE_WEDGE_THRESHOLD = 1 / 60; // 1 minute in hours

export interface RouteMetricSummary
{
    /** given in miles */
    distanceMi: number;
    filteredDistanceMi: number;
    /** total route duration */
    totalDurationMs: number;
    filteredDurationMs: number;
    /** total driving duration */
    drivingDurationMs: number;
    /** total weather delay duration */
    totalWeatherDelayDurationMs: number;
    /** total time spent in overnights */
    overnightStayDurationMs: number;
    /** total number of overnights */
    numberOfOvernights: number;
    /** total number of Weather Delays */
    numberOfDelays: number;
    /** Highest Impact level observed along route */
    maxImpactObserved: RiskCategory;
    travelDurationMsInRiskZoneBeyondForecast: number;
    travelDurationMsInRiskZoneNone: number;
    travelDurationMsInRiskZoneLow: number;
    travelDurationMsInRiskZoneLowMedium: number;
    travelDurationMsInRiskZoneMedium: number;
    travelDurationMsInRiskZoneMediumHigh: number;
    travelDurationMsInRiskZoneHigh: number;
    travelDurationMsInRiskZoneGoldilocks: number;
    travelDistanceMiInRiskZoneBeyondForecast: number;
    travelDistanceMiInRiskZoneNone: number;
    travelDistanceMiInRiskZoneLow: number;
    travelDistanceMiInRiskZoneLowMedium: number;
    travelDistanceMiInRiskZoneMedium: number;
    travelDistanceMiInRiskZoneMediumHigh: number;
    travelDistanceMiInRiskZoneHigh: number;
    travelDistanceMiInRiskZoneGoldilocks: number;
    metricsByDate: Array<TimeRangeMetrics>;
}

const calculateTravelMetrics = (
    routeMetrics: Array<RouteMetrics>,
    property: keyof RiskCategoryMetrics,
    riskCategory?: RiskCategory,
) =>
{
    return routeMetrics
        .flatMap( each => each.timeSegmentMetrics )
        .flatMap( each => each.riskCategoryMetrics )
        .filter( each => ( riskCategory !== undefined ? each.riskCategory === riskCategory : true ) )
        .reduce( ( sum, currentCategory ) => sum + ( currentCategory[property] ), 0 );
};

export const getTravelDurationByRiskCategory = ( routeMetrics: Array<RouteMetrics>, riskCategory: RiskCategory ) =>
{
    return calculateTravelMetrics( routeMetrics, "durationMs", riskCategory );
};

export const getTravelDurationForAllCategory = ( routeMetrics: Array<RouteMetrics> ) =>
{
    return calculateTravelMetrics( routeMetrics, "durationMs" );
};

export const getTravelDistanceByRiskCategory = ( routeMetrics: Array<RouteMetrics>, riskCategory: RiskCategory ) =>
{
    return calculateTravelMetrics( routeMetrics, "distanceMi", riskCategory );
};

export const getTravelDistanceForAllCategory = ( routeMetrics: Array<RouteMetrics> ) =>
{
    return calculateTravelMetrics( routeMetrics, "distanceMi" );
};




/* Calculate the sum of each metric across all segments.
 * Since metrics are reported per segment, it's nice to also see totals for the entire route.
 */
export function getSummaryRouteMetrics(
    routeMetrics: Array<RouteMetrics>,
    selectedDates: Set<number> | undefined,
): RouteMetricSummary
{
    let filteredMetrics = routeMetrics.map( ( route ) =>
    {
        if ( selectedDates )
        {
            return {
                ...route,
                timeSegmentMetrics: route.timeSegmentMetrics.filter( ( segment ) =>
                {
                    const segmentDate = segment.startTime;
                    if ( segmentDate !== undefined )
                    {
                        const segmentDateObj = new Date( segmentDate );
                        return selectedDates.has( segmentDateObj.getTime() );
                    }

                } ),
            };
        }
        return route; // Return the original route if no dates are selected
    } ).filter( route => route.timeSegmentMetrics.length > 0 );
    if ( filteredMetrics.length === 0 )
    {
        //we are seeing old data that has been migrated to the per-day schema
        filteredMetrics = routeMetrics;
    }

    return {
        distanceMi: routeMetrics
            .map( ( each ) => each.distanceMi.valueOf() )
            .reduce( ( sum, current ) => sum + current, 0 ),
        filteredDistanceMi: getTravelDistanceForAllCategory( filteredMetrics ),
        totalDurationMs: routeMetrics
            .map( ( each ) => each.totalDurationMs )
            .reduce( ( sum, current ) => sum + current, 0 ),
        filteredDurationMs: getTravelDurationForAllCategory( filteredMetrics ),
        drivingDurationMs: routeMetrics
            .map( ( each ) => each.drivingDurationMs )
            .reduce( ( sum, current ) => sum + current, 0 ),
        totalWeatherDelayDurationMs: routeMetrics
            .map( ( each ) => each.totalWeatherDelayDurationMs.valueOf() )
            .reduce( ( sum, current ) => sum + current, 0 ),
        overnightStayDurationMs: routeMetrics
            .map( ( each ) => each.overnightStayDurationMs.valueOf() )
            .reduce( ( sum, current ) => sum + current, 0 ),
        numberOfOvernights: routeMetrics
            .map( ( each ) => each.numberOfOvernights.valueOf() )
            .reduce( ( sum, current ) => sum + current, 0 ),
        numberOfDelays: routeMetrics
            .map( ( each ) => each.numberOfDelays.valueOf() )
            .reduce( ( sum, current ) => sum + current, 0 ),
        maxImpactObserved: routeMetrics
            .map( ( each ) => each.maxRiskObserved.valueOf() )
            .reduce( ( max, current ) => Math.max( max, current ), 0 ),
        travelDurationMsInRiskZoneBeyondForecast: getTravelDurationByRiskCategory( filteredMetrics, RiskCategory.RISK_CATEGORY_UNSPECIFIED ),
        travelDurationMsInRiskZoneNone: getTravelDurationByRiskCategory( filteredMetrics, RiskCategory.RISK_CATEGORY_NONE ),
        travelDurationMsInRiskZoneLow: getTravelDurationByRiskCategory( filteredMetrics, RiskCategory.RISK_CATEGORY_LOW ),
        travelDurationMsInRiskZoneLowMedium: getTravelDurationByRiskCategory( filteredMetrics, RiskCategory.RISK_CATEGORY_LOW_MEDIUM ),
        travelDurationMsInRiskZoneMedium: getTravelDurationByRiskCategory( filteredMetrics, RiskCategory.RISK_CATEGORY_MEDIUM ),
        travelDurationMsInRiskZoneMediumHigh: getTravelDurationByRiskCategory( filteredMetrics, RiskCategory.RISK_CATEGORY_MEDIUM_HIGH ),
        travelDurationMsInRiskZoneHigh: getTravelDurationByRiskCategory( filteredMetrics, RiskCategory.RISK_CATEGORY_HIGH ),
        travelDurationMsInRiskZoneGoldilocks: getTravelDurationByRiskCategory( filteredMetrics, RiskCategory.RISK_CATEGORY_GOLDILOCKS ),
        travelDistanceMiInRiskZoneBeyondForecast: getTravelDurationByRiskCategory( filteredMetrics, RiskCategory.RISK_CATEGORY_UNSPECIFIED ),
        travelDistanceMiInRiskZoneNone: getTravelDistanceByRiskCategory( filteredMetrics, RiskCategory.RISK_CATEGORY_NONE ),
        travelDistanceMiInRiskZoneLow: getTravelDistanceByRiskCategory( filteredMetrics, RiskCategory.RISK_CATEGORY_LOW ),
        travelDistanceMiInRiskZoneLowMedium: getTravelDistanceByRiskCategory( filteredMetrics, RiskCategory.RISK_CATEGORY_LOW_MEDIUM ),
        travelDistanceMiInRiskZoneMedium: getTravelDistanceByRiskCategory( filteredMetrics, RiskCategory.RISK_CATEGORY_MEDIUM ),
        travelDistanceMiInRiskZoneMediumHigh: getTravelDistanceByRiskCategory( filteredMetrics, RiskCategory.RISK_CATEGORY_MEDIUM_HIGH ),
        travelDistanceMiInRiskZoneHigh: getTravelDistanceByRiskCategory( filteredMetrics, RiskCategory.RISK_CATEGORY_HIGH ),
        travelDistanceMiInRiskZoneGoldilocks: getTravelDistanceByRiskCategory( filteredMetrics, RiskCategory.RISK_CATEGORY_GOLDILOCKS ),
        metricsByDate: filteredMetrics.flatMap( route => route.timeSegmentMetrics ),
    };
}



export function getDays( routeMetrics: Array<RouteMetrics> )
{
    const dateList: Array<Date> = [];

    routeMetrics.forEach( ( each ) =>
    {
        each.timeSegmentMetrics.forEach( ( segment ) =>
        {
            if ( segment.startTime !== undefined )
            {
                // In older versions of this code, the frontend had to handle normalizing dates because the backend passed
                // data by segment rather than by day. Now that the backend handles separating metrics by day, we do not
                // do any normalization or handling of timezones on the frontend.
                const segmentDate = new Date( segment.startTime );

                if ( !dateList.some( ( date ) => date.getTime() === segmentDate.getTime() ) )
                {
                    dateList.push( segmentDate );
                }
            }
        } );
    } );

    return dateList;
}
